/**
 * §A.7 · Sembrar el censo federativo, y cómo va el directorio.
 *
 * ## «Un directorio con cinco clubes es peor que no tener directorio»
 *
 * Parece abandonado y nos hace parecer pequeños. Esta ruta es lo que hace que el
 * buscador nazca lleno, y el contrato la llama «probablemente la tarea más valiosa
 * del mes de Manuel».
 *
 * ## El pegado de JSON no es pereza de interfaz
 *
 * El cuerpo es una lista de hasta 500 clubes con sede, coordenadas y disciplinas. Un
 * formulario para teclear eso no existe: el censo llega **como fichero** de una
 * federación, y hasta que §S.3 dé por dónde subirlo, pegar el JSON es la forma
 * honesta de usar la ruta. Se dice en pantalla, con la forma esperada delante.
 *
 * Lo que sí hace la pantalla es **no dejar enviar algo que no es el contrato**:
 * comprueba que sea un objeto con `source` y `clubs`, porque un `422` sobre 500 filas
 * no dice cuál.
 *
 * ## `skipped` no es un fallo
 *
 * «La fuente se importa varias veces —llega corregida, o se añade una provincia— y
 * volver a pasarla no puede duplicar clubes ni pisar fichas: alguno puede haber
 * reclamado la suya desde la última pasada, y sobrescribirla sería borrarle el
 * trabajo.» Así que el resultado lo dice con esas palabras.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { cuenta, numero } from '@/shared/ui/formato';
import type {
  EstadisticasDelDirectorio,
  ResultadoDeImportacion,
} from '@/backoffice/tiposDeDirectorio';
import {
  enLotes,
  fuenteDe,
  leerCenso,
  decodificar,
  plantilla,
  type LecturaDelCenso,
} from '@/backoffice/censoCsv';

/** El ejemplo que se enseña: lo mínimo del contrato, con una sede. */
const EJEMPLO = `{
  "source": "censo_fed_madrid",
  "clubs": [
    {
      "slug": "gimnasio-samurai",
      "trade_name": "Gimnasio Samurái",
      "phone": "914000000",
      "discipline_codes": ["karate"],
      "venue": {
        "name": "Sede principal",
        "city": "Madrid",
        "province": "Madrid",
        "latitude": 40.4168,
        "longitude": -3.7038
      }
    }
  ]
}`;

/** La forma de un `slug`, tal cual la exige el DTO. */
const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Lo que se puede comprobar sin el servidor: que tenga la forma del contrato.
 *
 * ## Y por qué merece la pena comprobar cada `slug` aquí
 *
 * Un fallo de **forma** lo rechaza TypeBox antes de entrar en el caso de uso, así que
 * **tumba el lote entero**: mandar 500 clubes con uno mal escrito no siembra ninguno.
 * Eso es distinto de un fallo de **negocio** —un club que ya existe, una disciplina
 * que no está dada de alta—, que sí se anota en `errors` y no frena a los demás.
 *
 * El servidor avisa bien —«clubs/2/slug: Expected string to match…»— pero en un censo
 * de quinientas filas, «la 2» es una fila que hay que ir a contar. Aquí se nombra el
 * club. Se vio probándolo el 18-09-2026, metiendo un `slug` con espacios a propósito.
 */
function revisar(texto: string): { cuerpo?: unknown; error?: string } {
  if (texto.trim() === '') return {};
  let cuerpo: unknown;
  try {
    cuerpo = JSON.parse(texto) as unknown;
  } catch {
    return {
      error: 'Eso no es JSON válido. Suele ser una coma de más al final.',
    };
  }
  if (typeof cuerpo !== 'object' || cuerpo === null || Array.isArray(cuerpo)) {
    return { error: 'Tiene que ser un objeto con «source» y «clubs».' };
  }
  const o = cuerpo as Record<string, unknown>;
  if (typeof o['source'] !== 'string' || o['source'].trim().length < 3) {
    return {
      error:
        'Falta «source»: de dónde sale este censo. Dentro de un año es lo único que dice de dónde vino cada registro.',
    };
  }
  if (!Array.isArray(o['clubs']) || o['clubs'].length === 0) {
    return { error: 'Falta «clubs», que es la lista de clubes a sembrar.' };
  }
  if (o['clubs'].length > 500) {
    return {
      error: `Son ${o['clubs'].length} clubes y el tope es 500. Pártelo en dos pasadas: repetir la fuente no duplica nada.`,
    };
  }

  /*
   * Y cada club, porque un fallo de forma no deja pasar ni uno. Se nombran hasta tres
   * y se dice cuántos quedan: una lista de cuarenta errores no se lee.
   */
  const malos: string[] = [];
  for (const [i, c] of (o['clubs'] as unknown[]).entries()) {
    if (typeof c !== 'object' || c === null || Array.isArray(c)) {
      malos.push(`el ${i + 1}.º no es un objeto`);
      continue;
    }
    const club = c as Record<string, unknown>;
    const nombre =
      typeof club['trade_name'] === 'string' && club['trade_name'].trim() !== ''
        ? club['trade_name']
        : `el ${i + 1}.º`;
    if (typeof club['trade_name'] !== 'string') {
      malos.push(`${nombre}: le falta «trade_name»`);
    } else if (typeof club['slug'] !== 'string' || !SLUG.test(club['slug'])) {
      malos.push(`${nombre}: el «slug» tiene que ser «asi-en-minusculas»`);
    }
  }

  if (malos.length > 0) {
    const muestra = malos.slice(0, 3).join(' · ');
    return {
      error:
        `${malos.length === 1 ? 'Hay un club con la forma mal' : `Hay ${malos.length} clubes con la forma mal`}, ` +
        `y eso no siembra ninguno: ${muestra}` +
        (malos.length > 3 ? ` · y ${malos.length - 3} más.` : '.'),
    };
  }

  return { cuerpo };
}

/** Descarga la plantilla, hecha aquí: ni un fichero más en el servidor. */
function descargarPlantilla() {
  const url = URL.createObjectURL(
    new Blob([plantilla()], { type: 'text/csv;charset=utf-8' })
  );
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = 'plantilla-censo-kiap.csv';
  document.body.append(enlace);
  enlace.click();
  enlace.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Lo que dice la hoja antes de sembrar: qué entra, qué se avisa y qué no. */
function ResumenDeLaHoja({ lectura }: { lectura: LecturaDelCenso }) {
  const conAvisos = lectura.clubes.filter((c) => c.avisos.length > 0);
  return (
    <div className="aviso" role="status">
      <p>
        <strong>
          {cuenta(lectura.clubes.length, 'club listo', 'clubes listos')} para
          sembrar
        </strong>
        {conAvisos.length > 0 &&
          ` · ${cuenta(conAvisos.length, 'no saldrá', 'no saldrán')} en el buscador hasta completarlo`}
        {lectura.descartadas.length > 0 &&
          ` · ${cuenta(lectura.descartadas.length, 'fila descartada', 'filas descartadas')}`}
        .
      </p>

      {conAvisos.length > 0 && (
        <ul className="lista-clara">
          {conAvisos.slice(0, 5).map((c) => (
            <li key={c.fila}>
              Fila {c.fila}, {c.club.trade_name}: {c.avisos.join(', ')}
            </li>
          ))}
          {conAvisos.length > 5 && <li>Y {conAvisos.length - 5} más.</li>}
        </ul>
      )}

      {lectura.descartadas.length > 0 && (
        <>
          <p className="tenue">
            <strong>Las descartadas no entran:</strong> corrígelas en la hoja y
            vuelve a subirla entera; lo ya sembrado se salta.
          </p>
          <ul className="lista-clara">
            {lectura.descartadas.slice(0, 5).map((d) => (
              <li key={d.fila}>
                Fila {d.fila}: {d.motivo}
              </li>
            ))}
            {lectura.descartadas.length > 5 && (
              <li>Y {lectura.descartadas.length - 5} más.</li>
            )}
          </ul>
        </>
      )}

      {lectura.ignoradas.length > 0 && (
        <p className="tenue">
          Columnas que no se usan: {lectura.ignoradas.join(', ')}.
        </p>
      )}
    </div>
  );
}

/** La suma de varios lotes, como si hubiera sido una sola importación. */
function sumar(
  a: ResultadoDeImportacion | null,
  b: ResultadoDeImportacion
): ResultadoDeImportacion {
  if (!a) return b;
  return {
    source: b.source,
    created: a.created + b.created,
    skipped: a.skipped + b.skipped,
    venues_created: a.venues_created + b.venues_created,
    published: a.published + b.published,
    errors: [...a.errors, ...b.errors],
  };
}

export function ImportarCenso() {
  const cache = useQueryClient();
  const [texto, setTexto] = useState('');
  const [resultado, setResultado] = useState<ResultadoDeImportacion | null>(
    null
  );
  const [problema, setProblema] = useState<Problema | null>(null);
  const [lectura, setLectura] = useState<LecturaDelCenso | null>(null);
  const [fuente, setFuente] = useState('');
  const [progreso, setProgreso] = useState<{
    hecho: number;
    total: number;
  } | null>(null);
  const [problemaHoja, setProblemaHoja] = useState<Problema | null>(null);

  const cargarFichero = async (fichero: File) => {
    setResultado(null);
    setProblemaHoja(null);
    setLectura(leerCenso(decodificar(await fichero.arrayBuffer())));
    if (fuente.trim() === '') setFuente(fuenteDe(fichero.name));
  };

  /*
   * Por lotes de 500, que es el tope del contrato, uno detrás de otro y cada uno con
   * su clave de idempotencia. Si uno falla, lo anterior ya está sembrado: volver a
   * subir la hoja entera es seguro, porque lo que ya estaba se salta.
   */
  const sembrarHoja = useMutation({
    mutationFn: async () => {
      if (!lectura) return null;
      const lotes = enLotes(lectura.clubes.map((c) => c.club));
      let total: ResultadoDeImportacion | null = null;
      for (const [i, lote] of lotes.entries()) {
        setProgreso({ hecho: i, total: lotes.length });
        const r = await api.v1.admin.directory.import.post(
          { source: fuente.trim(), clubs: lote } as never,
          { headers: { 'idempotency-key': crypto.randomUUID() } }
        );
        if (r.error) {
          if (total) setResultado(total);
          throw new ErrorDeApi(aProblema(r.error));
        }
        total = sumar(total, r.data as unknown as ResultadoDeImportacion);
      }
      return total;
    },
    onSuccess: (r) => {
      setProgreso(null);
      if (r) setResultado(r);
      setLectura(null);
      void cache.invalidateQueries({ queryKey: ['admin', 'directorio'] });
      void cache.invalidateQueries({ queryKey: ['admin', 'clubes'] });
    },
    onError: (e) => {
      setProgreso(null);
      setProblemaHoja(aProblema(e));
    },
  });

  const stats = useQuery({
    queryKey: ['admin', 'directorio', 'stats'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin.directory.stats.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as EstadisticasDelDirectorio;
    },
  });

  const revision = revisar(texto);

  const importar = useMutation({
    mutationFn: async () => {
      const r = await api.v1.admin.directory.import.post(
        revision.cuerpo as never,
        // Crea hasta 500 clubes: un doble clic no puede sembrar el censo dos veces.
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as ResultadoDeImportacion;
    },
    onSuccess: (r) => {
      setResultado(r);
      setTexto('');
      void cache.invalidateQueries({ queryKey: ['admin', 'directorio'] });
      void cache.invalidateQueries({ queryKey: ['admin', 'clubes'] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  return (
    <section>
      <h1>El directorio</h1>

      {/* ── Cómo va ───────────────────────────────────────────────────────── */}
      {stats.isPending && <Cargando filas={2} etiqueta="Cargando" />}

      {stats.isError && (
        <PanelError
          problema={aProblema(stats.error)}
          reintentar={() => void stats.refetch()}
        />
      )}

      {stats.data && (
        <>
          <dl className="datos datos-grandes">
            <div>
              <dt>Listados</dt>
              <dd>{stats.data.listed}</dd>
            </div>
            <div>
              {/* Las dos juntas: un directorio que crece y una cartera que no es otra conversación. */}
              <dt>De ellos, clientes</dt>
              <dd>{stats.data.listed_customers}</dd>
            </div>
            <div>
              <dt>Reservas de prueba</dt>
              <dd>{stats.data.trial_bookings_last_30d}</dd>
            </div>
          </dl>

          <p className="tenue">
            {stats.data.listed > 0 && stats.data.listed_customers === 0 ? (
              <strong>
                El buscador tiene {cuenta(stats.data.listed, 'club', 'clubes')}{' '}
                y ninguno es cliente todavía. Eso no se arregla sembrando más:
                se arregla mirando qué pasa cuando alguien llega a una ficha.
              </strong>
            ) : (
              <>
                {stats.data.seeded} sembrados del censo · {stats.data.claimed}{' '}
                fichas reclamadas · {stats.data.claim_pending} reclamaciones
                pendientes · {stats.data.hidden} escondidas.
              </>
            )}
          </p>

          {stats.data.venues_pending_geocode > 0 && (
            <p className="tenue">
              {/*
                Sin coordenadas la sede no sale en una búsqueda por cercanía: el club
                está sembrado y es invisible, que es lo peor de los dos mundos.
              */}
              <strong>
                {cuenta(stats.data.venues_pending_geocode, 'sede', 'sedes')} sin
                coordenadas.
              </strong>{' '}
              No salen en una búsqueda por cercanía: están sembradas y son
              invisibles.
            </p>
          )}

          {stats.data.trial_bookings > 0 && (
            <p className="tenue">
              {stats.data.trial_bookings} reservas de clase de prueba en total,{' '}
              {stats.data.bookings_converted} convertidas en alta. Convierte el{' '}
              {numero(
                (stats.data.bookings_converted / stats.data.trial_bookings) *
                  100,
                0
              )}{' '}
              %.
            </p>
          )}

          {stats.data.by_province.length > 0 && (
            <div className="tabla-marco">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Provincia</th>
                    <th className="num">Listados</th>
                    <th className="num">Clientes</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.data.by_province.map((p) => (
                    <tr key={p.province}>
                      <td>{p.province}</td>
                      <td className="num">{p.listed}</td>
                      <td className="num">{p.customers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Sembrar ───────────────────────────────────────────────────────── */}
      <h2 className="seccion">Sembrar un censo</h2>

      {resultado && (
        <div className="aviso" role="status">
          <p>
            <strong>
              {resultado.created === 0
                ? 'No se ha creado ningún club nuevo'
                : `${cuenta(resultado.created, 'club creado', 'clubes creados')}`}
            </strong>
            {resultado.venues_created > 0 &&
              ` · ${cuenta(resultado.venues_created, 'sede creada', 'sedes creadas')}`}
            {resultado.created > 0 &&
              ` · ${cuenta(resultado.published, 'publicada', 'publicadas')} en el buscador`}
            {resultado.skipped > 0 && (
              <>
                {' · '}
                {cuenta(resultado.skipped, 'saltado', 'saltados')}
              </>
            )}
            .
          </p>

          {resultado.published < resultado.created && (
            <p className="tenue">
              {/* Para que una ficha sin publicar no se lea como un club perdido. */}
              <strong>Las que no se han publicado</strong> quedan sembradas. Si
              solo les faltan las coordenadas, las pone el proceso de esta noche
              y salen solas. Si les falta la disciplina, el municipio o un
              teléfono o web, no saldrán hasta que se completen o hasta que el
              club reclame su ficha y la publique él.
            </p>
          )}

          {resultado.skipped > 0 && (
            <p className="tenue">
              {/* Lo que hay que explicar para que no se lea como un fallo. */}
              <strong>Los saltados no son un fallo.</strong> Ya estaban, y no se
              pisan: alguno puede haber reclamado su ficha desde la última
              pasada, y sobrescribirla sería borrarle el trabajo.
            </p>
          )}

          {resultado.errors.length > 0 && (
            <>
              <p>
                <strong>
                  {cuenta(resultado.errors.length, 'fila', 'filas')} no{' '}
                  {resultado.errors.length === 1 ? 'entró' : 'entraron'}:
                </strong>
              </p>
              <ul className="lista-clara">
                {resultado.errors.map((e) => (
                  <li key={e.slug}>
                    <code>{e.slug}</code> · {e.reason}
                  </li>
                ))}
              </ul>
              <p className="tenue">
                El resto sí entró:{' '}
                <strong>un fallo de estos no frena a los demás</strong>, al
                contrario que uno de forma —un «slug» mal escrito—, que rechaza
                el lote completo antes de empezar. Corrige estas y vuelve a
                pasar la fuente entera.
              </p>
            </>
          )}

          <div className="aviso-pie">
            <span className="tenue">Fuente: {resultado.source}</span>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setResultado(null)}
            >
              Vale
            </button>
          </div>
        </div>
      )}

      <p className="tenue">
        Un directorio con cinco clubes es peor que no tener directorio: parece
        abandonado. Los clubes nacen <strong>sin nadie detrás</strong>, con la
        ficha sembrada del censo, y su maestro puede reclamarla.
      </p>

      <p className="tenue">
        El censo llega como hoja de cálculo: guárdala como CSV (en Excel,{' '}
        <em>Archivo → Guardar como → CSV</em>) y súbela aquí. Solo hace falta el
        nombre del club. <strong>Las coordenadas no hacen falta</strong>: las
        pone el proceso de cada noche, y la ficha sale en el buscador en cuanto
        las tiene, si tiene disciplina, municipio y teléfono o web.
      </p>

      <div className="acciones">
        <button
          type="button"
          className="btn-plano"
          onClick={descargarPlantilla}
        >
          Descargar la plantilla
        </button>
      </div>

      <div className="formulario">
        <label>
          La hoja, en CSV
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const fichero = e.target.files?.[0];
              if (fichero) void cargarFichero(fichero);
            }}
          />
        </label>

        <label>
          De dónde sale
          <input
            type="text"
            value={fuente}
            maxLength={40}
            placeholder="censo_fed_madrid_2026"
            onChange={(e) => setFuente(e.target.value)}
          />
        </label>
        <p className="tenue">
          Queda guardado en cada ficha: dentro de un año es lo único que dirá de
          dónde salió ese club.
        </p>

        {lectura?.error && (
          <p>
            <strong>{lectura.error}</strong>
          </p>
        )}

        {lectura && !lectura.error && <ResumenDeLaHoja lectura={lectura} />}

        {problemaHoja && <PanelError problema={problemaHoja} />}
      </div>

      <div className="acciones">
        <button
          type="button"
          disabled={
            sembrarHoja.isPending ||
            !lectura ||
            lectura.clubes.length === 0 ||
            fuente.trim().length < 3
          }
          onClick={() => {
            setProblemaHoja(null);
            setResultado(null);
            sembrarHoja.mutate();
          }}
        >
          {sembrarHoja.isPending && progreso
            ? `Sembrando… lote ${progreso.hecho + 1} de ${progreso.total}`
            : lectura && lectura.clubes.length > 0
              ? `Sembrar ${cuenta(lectura.clubes.length, 'club', 'clubes')}`
              : 'Sembrar el censo'}
        </button>
      </div>

      <details>
        <summary>Avanzado: pegar el censo en JSON</summary>
        <details>
          <summary>Ver la forma que espera</summary>
          <pre className="bloque-codigo">
            <code>{EJEMPLO}</code>
          </pre>
          <p className="tenue">
            De cada club solo hacen falta <code>slug</code> y{' '}
            <code>trade_name</code>. Las instalaciones —vestuario, aparcamiento—
            se quedan sin poner a propósito:{' '}
            <strong>«no lo sé» no es «no lo tiene»</strong>.
          </p>
        </details>

        {/*
        `.formulario` envuelve el campo: es la clase que le da forma a un bloque de
        campos, y un `<label>` suelto dentro de una `<section>` no la tiene —salía
        «El censo» al lado de su cuadro en vez de encima—.
      */}
        <div className="formulario">
          <label>
            El censo
            <textarea
              rows={12}
              className="entrada-codigo"
              placeholder={EJEMPLO}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
          </label>

          {revision.error && (
            <p className="tenue">
              <strong>{revision.error}</strong>
            </p>
          )}

          {problema && <PanelError problema={problema} />}
        </div>

        <div className="acciones">
          <button
            type="button"
            disabled={importar.isPending || revision.cuerpo === undefined}
            onClick={() => {
              setProblema(null);
              setResultado(null);
              importar.mutate();
            }}
          >
            {importar.isPending ? 'Sembrando…' : 'Sembrar el censo'}
          </button>
        </div>
      </details>
    </section>
  );
}
